import { useIsMobile } from "@/hooks/use-mobile";
import {
  actionsKeys,
  cashAppStatus,
  cn,
  formatAmount,
  getRequiredFields,
  MONTHS,
  permissionActions,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetParkedList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { SEARCH_CRITERIA, SEARCH_TYPE } from "./consts";
import { Badge } from "@/components/ui";

const useListing = () => {
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const hasAddPermission = useMemo(() => accessPermission?.[actionsKeys?.CASH_APP?.toLowerCase()][permissionActions.CREATE], [accessPermission]);

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetParkedList({
    communityId: communityDetail?.communityId as string,
    data: {
      filters: [
        {
          field: "documentType",
          value: "OA",
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "sourceSystem",
          value: "MANUAL_JE",
          operator: "NOT_EQUAL",
        },
        ...(form.watch()[SEARCH_CRITERIA]
          ? [
              {
                field: "updatedAt",
                value: [
                  moment(form.watch()[SEARCH_CRITERIA]?.from).format(
                    "YYYY-MM-DDT00:00:00"
                  ),
                  moment(form.watch()[SEARCH_CRITERIA]?.to).format(
                    "YYYY-MM-DDT23:59:00"
                  ),
                ],
                operator: "BETWEEN",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    form.setValue(SEARCH_TYPE, "createdAt");
    setTimeout(() => refetchDoc(), 100);
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc());
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.collection}`,
        name: "Collection",
      },
      {
        link: "",
        name: <span className="text-primary">Cash App</span>,
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}/${routes.collection}`);
  };

  // Define Table Columns
  const columns = [
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row?.parkDocumentHeaderId || ""),
    },
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) =>
        String(row?.status?.toLowerCase() == "posted" ? row?.documentNumber || "" : ""),
    },
    {
      accessorKey: "postingPeriod",
      header: <div>Billing Period</div>,
      accessorFn: (row: any) => MONTHS[row?.postingPeriod - 1] || "",
    },
    {
      accessorKey: "propertyProcessed",
      header: <div># Processed Properties</div>,
      accessorFn: (row: any) => String(row?.propertyProcessed || "0"),
    },
    {
      accessorKey: "updatedAt",
      header: <div>Processed Date</div>,
      accessorFn: (row: any) =>
        row?.updatedAt
          ? moment(row?.updatedAt || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "amount",
      meta: { align: "right" },
      header: <div>Collected Amount</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || "0"),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            cashAppStatus[row.row.original.status?.toLowerCase()]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {cashAppStatus[row?.row?.original?.status?.toLowerCase()]?.label}
        </Badge>
      ),
    },
  ];

  const onRowClick = (row: any) => {
    const id =
      row?.original?.status?.toLowerCase() == "posted"
        ? row?.original?.postDocumentHeaderId
        : row?.original?.parkDocumentHeaderId;
    navigate(`${id}/${row?.original?.status?.toLowerCase()}/view`);
  };

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    communityDetail,
    onAdd,
    onBackClick,
    hasAddPermission,
    currentFinanceYear
  };
};

export default useListing;
